import './RadialMenu.css'
import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faChevronUp,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { LAYOUT, COLOR, undoMoveDirection } from '../../config/uiTokens'
import { BATTLEFIELD_COUNT } from '../../rules/gameConfig'

// ─── Button definitions per variant ──────────────────────────────────────────

// Buying rockets/drones lives on the yield-indicator tabs (BattleScreen), not
// here — the radial menus only ever UNDO a pending purchase.
//
// When a front-line missile shares a structure subplot with a pending rocket or
// drone, the missile's own menu is built as normal and the undo button is
// appended. It sits at 'down' (centred below the cell, its usual home) when
// that slot is free, and only falls back to a diagonal when the missile's menu
// already uses 'down', so it never collides. `withRocket` / `withDrone` is 'undo'.
function getButtonDefs(args) {
    let defs = baseButtonDefs(args)
    if (args.withRocket === 'undo' || args.withDrone === 'undo') {
        const side = defs.some(d => d.side === 'down') ? 'down-right' : 'down'
        const action = args.withDrone === 'undo' ? 'undoDrone' : 'undoRocket'
        const label  = args.withDrone === 'undo' ? 'UNDO\nDRONE' : 'UNDO\nROCKET'
        defs.push({ side, action, label, icon: faXmark, color: COLOR.BTN_ATTACK, reverse: true })
    }
    return defs
}

function baseButtonDefs({ variant, movedFromId, currentId, hasReserve }) {
    if (variant === 'undoRocket') {
        // Confirm popped when you tap a pending (this-round) rocket to take it back.
        return [
            { side: 'down', action: 'undoRocket', label: 'UNDO\nROCKET', icon: faXmark, color: COLOR.BTN_ATTACK, reverse: true },
        ]
    }

    if (variant === 'undoDrone') {
        // Confirm popped when you tap a pending (this-round) drone to take it back.
        return [
            { side: 'down', action: 'undoDrone', label: 'UNDO\nDRONE', icon: faXmark, color: COLOR.BTN_ATTACK, reverse: true },
        ]
    }

    if (variant === 'deployArmy') {
        // A newly deployed missile only offers REMOVE — greens can't move.
        return [
            { side: 'up', action: 'remove', label: 'REMOVE', icon: faXmark, color: COLOR.BTN_PLAN },
        ]
    }

    if (variant === 'deployReserve') {
        return [
            { side: 'up', action: 'remove', label: 'REMOVE', icon: faXmark, color: COLOR.BTN_PLAN   },
        ]
    }

    // Attack is a corridor-level command carried by the corridor badge at the
    // divider — these two variants anchor there, not on a tile. ATTACK springs
    // up into the opp half; once armed, the badge pops UNDO instead.
    if (variant === 'corridorAttack') {
        return [
            { side: 'up', action: 'attack', label: 'ATTACK', icon: faChevronUp, color: COLOR.BTN_ATTACK },
        ]
    }

    if (variant === 'attacking') {
        return [
            { side: 'up', action: 'undoAttack', label: 'UNDO\nATTACK', icon: faChevronDown, color: COLOR.BTN_ATTACK, reverse: true },
        ]
    }

    // A moved-out corridor keeps its build option: 2+ veterans still standing
    // can fund a Base even after the corridor's move is spent.
    if (variant === 'buildOnly') {
        return [
            { side: 'down', action: 'buildFort', label: 'BROOD', icon: faChevronDown, color: COLOR.BTN_FORT },
        ]
    }

    // UNDO BASE sits just above the construction's bar, centred on it (the
    // hex board anchors it on the bar's centre with its own offset) — a
    // plain label, no direction glyph: the Base is where it is.
    if (variant === 'undoBuildFort') {
        return [
            { side: 'up', action: 'undoBuildFort', label: 'UNDO\nBROOD', icon: null, color: COLOR.BTN_FORT },
        ]
    }

    if (variant === 'moved') {
        const undoSide = undoMoveDirection(movedFromId, currentId)  // 'left' | 'right'
        const undoIcon = undoSide === 'left' ? faChevronLeft : faChevronRight
        const buttons = [
            { side: undoSide, action: 'undoMove', label: 'UNDO\nMOVE', icon: undoIcon, color: COLOR.BTN_MOVE },
        ]
        // A moved-in drone never funds a brood (the board passes hasReserve
        // false for it); the option is kept for a legacy caller that says so.
        if (hasReserve) {
            buttons.push({ side: 'down', action: 'buildFort', label: 'BROOD', icon: faChevronDown, color: COLOR.BTN_FORT })
        }
        return buttons
    }

    // Reserve moved-in: undo move only — attack belongs to the lead
    if (variant === 'reserveUndo') {
        const undoSide = undoMoveDirection(movedFromId, currentId)
        const undoIcon = undoSide === 'left' ? faChevronLeft : faChevronRight
        return [
            { side: undoSide, action: 'undoMove', label: 'UNDO\nMOVE', icon: undoIcon, color: COLOR.BTN_MOVE },
        ]
    }

    // Normal. The player picks the move direction per move: one MOVE button on
    // each side. Edge corridors offer only the inward one (no wrap-around —
    // corridor 1 can't move left, corridor 7 can't move right). No ATTACK —
    // that lives on the corridor badge.
    const defs = []
    if (currentId > 1) {
        defs.push({ side: 'left',  action: 'moveLeft',  label: 'MOVE', icon: faChevronLeft,  color: COLOR.BTN_MOVE_READY })
    }
    if (currentId < BATTLEFIELD_COUNT) {
        defs.push({ side: 'right', action: 'moveRight', label: 'MOVE', icon: faChevronRight, color: COLOR.BTN_MOVE_READY })
    }
    if (hasReserve) {
        defs.push({ side: 'down', action: 'buildFort', label: 'BROOD', icon: faChevronDown, color: COLOR.BTN_FORT })
    }
    return defs
}

// ─── Button position ──────────────────────────────────────────────────────────

// A button that would fall off the container's bottom edge (a menu anchored
// on the back row of the Grid — the hex board clips at its container, and the
// submit row sits right under it) opens on the mirrored side instead: down →
// up, down-right → up-right. Only with a real anchor and a measured container.
function mirrorIfClipped(side, cy, off, containerH, anchored) {
    const R = LAYOUT.RADIAL_BTN_RADIUS
    if (!anchored || !(containerH > 0)) return side
    if (side === 'down' && cy + off + R > containerH) return 'up'
    if (side === 'down-right' && cy + off * 0.7071 + R > containerH) return 'up-right'
    return side
}

function buttonPosition(side, cx, cy, off, containerW, containerH, isDesktop) {
    const R = LAYOUT.RADIAL_BTN_RADIUS
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))
    switch (side) {
        case 'up':    return { x: cx, y: isDesktop ? cy - off : clamp(cy - off, R, containerH - R) }
        case 'down':  return { x: cx, y: isDesktop ? cy + off : clamp(cy + off, R, containerH - R) }
        case 'left':  return { x: isDesktop ? cx - off : clamp(cx - off, R, containerW - R), y: cy }
        case 'right': return { x: isDesktop ? cx + off : clamp(cx + off, R, containerW - R), y: cy }
        // Diagonal — sits in the gap between two cardinals (used for the rocket button
        // in a combined missile+structure menu). Offset by off/√2 on each axis so all
        // buttons rest on the same radius.
        case 'down-right': {
            const d = off * 0.7071
            return {
                x: isDesktop ? cx + d : clamp(cx + d, R, containerW - R),
                y: isDesktop ? cy + d : clamp(cy + d, R, containerH - R),
            }
        }
        case 'up-right': {
            const d = off * 0.7071
            return {
                x: isDesktop ? cx + d : clamp(cx + d, R, containerW - R),
                y: isDesktop ? cy - d : clamp(cy - d, R, containerH - R),
            }
        }
        default:      return { x: cx, y: cy }
    }
}

// ─── RadialMenu ───────────────────────────────────────────────────────────────
//
// Rendered as a direct child of the grid container div so that
// position:absolute coordinates align with the grid.
//
// Props:
//   variant          'normal' | 'moved' | 'undoRocket' | 'undoDrone' | ...
//   col              0–6 — column of the tapped cell
//   row              0–9 — row of the tapped cell
//   movedFromId      number — origin corridor id (moved variant only)
//   currentId        number — this corridor id (moved variant only)
//   hasReserve       bool — show Build Fort button (normal variant only)
//   containerWidth   px — grid container offsetWidth
//   containerHeight  px — grid container offsetHeight
//   anchorX/anchorY  px — optional explicit centre (container coords); the hex
//                    board has no 7×10 cells, so it anchors menus on its own
//                    geometry (a drone tile's centre, a lane's cap) and only
//                    keeps `col` for the corridor darkening
//   offset           px — optional distance from the anchor to the buttons'
//                    centres, replacing the cell-derived one (the hex board
//                    hugs a Base's bar with it)
//   onAction         fn(actionString) — called when a button is tapped
//   onClose          fn — called when tapping outside the buttons
//
export default function RadialMenu({
    variant,
    col,
    row,
    anchorX = null,
    anchorY = null,
    movedFromId,
    currentId,
    hasReserve,
    containerWidth,
    containerHeight,
    isDesktop,
    withRocket,
    withDrone,
    offset = null,
    onAction,
    onClose,
}) {
    const colW = containerWidth  / LAYOUT.GRID_COLS
    const rowH = containerHeight / LAYOUT.GRID_ROWS

    // Centre of the tapped cell (or the explicit anchor)
    const cx = anchorX ?? (col * colW + colW / 2)
    const cy = anchorY ?? (row * rowH + rowH / 2)

    // Shared offset: half cell-width + button radius (no gap — side buttons kiss the cell edge)
    const off = offset ?? (Math.round(colW / 2 + LAYOUT.RADIAL_BTN_RADIUS) - 12)

    // Column overlay bounds
    const colLeft  = col * colW
    const colWidth = colW

    const btnDefs = getButtonDefs({ variant, movedFromId, currentId, hasReserve, withRocket, withDrone })

    // Trigger open animation after the first rendered frame (mirrors mockup's requestAnimationFrame).
    const [isOpen, setIsOpen] = useState(false)
    useEffect(() => {
        const id = requestAnimationFrame(() => setIsOpen(true))
        return () => cancelAnimationFrame(id)
    }, [])

    return (
        <div className="rm__root">

            {/* Full-screen dismiss layer — sits behind buttons. Dismisses on
                pointerdown, not click: on a phone the menu opens on the tap's
                pointerup, and the click the browser fires right after lands on
                this freshly mounted layer and would close the menu at once. */}
            <div className="rm__catcher" onPointerDown={onClose} />

            {/* Active column darkening */}
            <div
                className="rm__col-overlay"
                style={{ left: colLeft, width: colWidth }}
            />

            {/* Action buttons */}
            {btnDefs.map(({ side, action, label, icon, color, reverse }) => {
                const placed = mirrorIfClipped(side, cy, off, containerHeight, anchorY != null)
                const { x, y } = buttonPosition(placed, cx, cy, off, containerWidth, containerHeight, isDesktop)
                const lines = label.split('\n')
                const labelEl = (
                    <span className="rm__btn-label">
                        {lines.map((line, i) => (
                            <span key={i} className="rm__btn-line">{line}</span>
                        ))}
                    </span>
                )
                const iconEl = icon ? <FontAwesomeIcon icon={icon} className="rm__btn-icon" /> : null
                return (
                    <button
                        key={action}
                        className={`rm__btn${isOpen ? ' rm__btn--open' : ''}`}
                        style={{ left: x, top: y, color }}
                        onClick={(e) => { e.stopPropagation(); onAction(action) }}
                    >
                        {reverse ? <>{labelEl}{iconEl}</> : <>{iconEl}{labelEl}</>}
                    </button>
                )
            })}

        </div>
    )
}
